import { countRu, pluralRu, VIOLATION_FORMS } from '../../utils/pluralRu';
import { formatInsightFacts } from './summaryFormatters';
import { HIERARCHY_LABELS, withCameraWord } from '../../utils/hierarchyLabels';

/*
 * Модель карточек «На что обратить внимание» / «Позитивные сигналы» (ТЗ 58 №29/№30/№35).
 * Backend отдаёт insight с group и facts; текст explanation содержит стрелки и
 * английские метрики, поэтому карточка строится из facts, а не из explanation.
 */

/** Порядок групп — как в макете obzor-1. */
export const INSIGHT_GROUP_ORDER = ['recurrence', 'overall', 'weekday', 'shift', 'entity'];

export const INSIGHT_GROUP_TITLES = {
  attention: {
    recurrence: 'Повторяющиеся нарушения',
    overall: 'Рост частоты нарушений',
    weekday: 'Негативная динамика по дням недели',
    shift: 'Ухудшились показатели смен',
    entity: 'Ухудшились показатели камер и локаций',
  },
  positive: {
    overall: 'Снижение частоты нарушений',
    weekday: 'Позитивная динамика по дням недели',
    shift: 'Улучшились показатели смен',
    entity: 'Улучшились показатели камер и локаций',
  },
};

const OTHER_GROUP_TITLE = 'Другие сигналы';

/** Метрики из facts.metric — понятные названия единицы измерения. */
export const INSIGHT_METRIC_UNITS = {
  violations_per_100_control_hours: 'на 100 ч контроля',
};

export const NO_COMPARISON_TEXT = 'Нет сравнения';

/** Коды, у которых текст facts собирает formatInsightFacts (нет пары current/previous). */
const FACTS_LINE_CODES = new Set([
  'time_concentration', 'no_violations_after_previous_activity', 'cameras_without_violations',
]);

const isNum = (v) => v != null && v !== '' && Number.isFinite(Number(v));
const fmt1 = (v) => Number(v).toFixed(1).replace('.', ',');
const fmtInt = (v) => String(Math.round(Number(v)));

const BETTER_CODE = /_(better|improved)$/;
const WORSE_CODE = /_(worse|worsened)$/;

const direction = (insight) => {
  if (BETTER_CODE.test(insight.code || '')) return 'better';
  if (WORSE_CODE.test(insight.code || '')) return 'worse';
  return insight.severity === 'positive' ? 'better' : 'worse';
};

/** Пара «текущее/прошлое»: сначала rate (с ним согласован change_percent), затем счётчик нарушений, затем любая пара current_*. */
const pickPair = (facts) => {
  if ('current_rate' in facts) {
    return { kind: 'rate', current: facts.current_rate, previous: facts.previous_rate };
  }
  if ('current_violations_count' in facts) {
    return { kind: 'count', current: facts.current_violations_count, previous: facts.previous_violations_count };
  }
  const key = Object.keys(facts).find((k) => k.startsWith('current_') && isNum(facts[k]));
  if (!key) return null;
  const suffix = key.slice('current_'.length);
  return { kind: 'other', current: facts[key], previous: facts[`previous_${suffix}`] };
};

const describeChange = (insight, facts) => {
  const dir = direction(insight);
  let signed = null;
  if (isNum(facts.improvement_percent)) signed = -Math.abs(Number(facts.improvement_percent));
  else if (isNum(facts.change_percent)) signed = Number(facts.change_percent);
  if (signed == null) return null;
  const rounded = Math.round(Math.abs(signed));
  const sign = signed < 0 ? '−' : '+';
  return { label: `${rounded === 0 ? '' : sign}${rounded}%`, tone: dir === 'better' ? 'good' : 'bad' };
};

const sanitizeTitle = (text) => String(text || '').replace(/\s*[→←↑↓]\s*/g, ' / ').replace(/:\s+/g, ' — ');

/** Тип сущности словами, чтобы камера и локация с одним именем не сливались. */
const ENTITY_LEVEL = { location: 'room' };
const entityTitle = (type, name) => {
  if (type === 'camera') return withCameraWord(name);
  const label = HIERARCHY_LABELS[ENTITY_LEVEL[type] || type]?.one;
  return label ? `${label} ${name}` : name;
};

const cardTitle = (insight, facts) => {
  if (facts.weekday_name) return facts.weekday_name;
  const place = [facts.camera_name, facts.shift_name].filter(Boolean);
  if (place.length) return place.join(' — ');
  const where = [facts.object_name, facts.location_name].filter(Boolean);
  if (where.length) return where.join(' — ');
  const named = facts.entity_name || facts.name;
  if (named) return entityTitle(facts.entity_type, String(named));
  return sanitizeTitle(insight.title);
};

/**
 * Одна карточка или null, если измеримых facts нет (методические/observe-сигналы).
 * previous = null → previousText «Нет сравнения», без чипа изменения.
 */
export const buildInsightCard = (insight, cameraNames = {}) => {
  const facts = insight?.facts;
  if (!facts || typeof facts !== 'object') return null;
  const base = {
    code: insight.code,
    severity: insight.severity,
    title: cardTitle(insight, facts),
    drilldown: insight.drilldown || null,
  };

  if (FACTS_LINE_CODES.has(insight.code)) {
    const line = formatInsightFacts(insight.code, facts, cameraNames);
    return line ? { ...base, title: sanitizeTitle(insight.title), current: null, factsLine: line } : null;
  }

  const pair = pickPair(facts);
  if (!pair || !isNum(pair.current)) return null;

  const hasPrevious = isNum(pair.previous);
  const metricUnit = INSIGHT_METRIC_UNITS[facts.metric];
  let currentText;
  let unit;
  let previousValue;
  if (pair.kind === 'rate') {
    currentText = fmt1(pair.current);
    unit = metricUnit || 'на 100 ч контроля';
    previousValue = hasPrevious ? fmt1(pair.previous) : null;
  } else if (pair.kind === 'count') {
    currentText = fmtInt(pair.current);
    unit = pluralRu(Number(pair.current), VIOLATION_FORMS);
    previousValue = hasPrevious ? fmtInt(pair.previous) : null;
  } else {
    currentText = fmt1(pair.current);
    unit = '';
    previousValue = hasPrevious ? fmt1(pair.previous) : null;
  }

  const secondary = pair.kind === 'rate' && isNum(facts.current_violations_count)
    ? `${countRu(Number(facts.current_violations_count), VIOLATION_FORMS)}${isNum(facts.previous_violations_count) ? `, было ${fmtInt(facts.previous_violations_count)}` : ''}`
    : '';

  return {
    ...base,
    current: { value: currentText, unit },
    previousText: hasPrevious ? `Было ${previousValue} в прошлом периоде` : NO_COMPARISON_TEXT,
    change: hasPrevious ? describeChange(insight, facts) : null,
    secondary,
  };
};

/**
 * Группы карточек по insight.group в порядке макета. repeated_violation_pattern
 * не выводится: паттерны рендерятся из repeated_patterns[] (ТЗ 58 №15).
 */
export const buildInsightGroups = (items, severityKind = 'attention', cameraNames = {}) => {
  const titles = INSIGHT_GROUP_TITLES[severityKind] || {};
  const byGroup = new Map();
  (Array.isArray(items) ? items : []).forEach((insight) => {
    if (!insight || insight.code === 'repeated_violation_pattern') return;
    const card = buildInsightCard(insight, cameraNames);
    if (!card) return;
    const group = insight.group || (/^violation_rate_/.test(insight.code || '') ? 'overall' : 'other');
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push(card);
  });
  const rank = (g) => {
    const i = INSIGHT_GROUP_ORDER.indexOf(g);
    return i === -1 ? INSIGHT_GROUP_ORDER.length : i;
  };
  return [...byGroup.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map((group) => ({ group, title: titles[group] || OTHER_GROUP_TITLE, cards: byGroup.get(group) }));
};
